import { OktaAuth } from '@okta/okta-auth-js';
import oktaConfig from '../../config/okta-config';

// Initialize Okta Auth instance
const oktaAuth = new OktaAuth(oktaConfig);

/**
 * Okta Authentication Service
 * Provides methods for authentication, token management, and user info
 */
class OktaAuthService {
  constructor() {
    this.oktaAuth = oktaAuth;
  }

  /**
   * Initialize the authentication service
   * Call this on app startup
   */
  async initialize() {
    try {
      await this.oktaAuth.start();
      return true;
    } catch (error) {
      console.error('Failed to initialize Okta Auth:', error);
      return false;
    }
  }

  /**
   * Sign in with redirect to Okta hosted login page
   */
  async signInWithRedirect() {
    try {
      await this.oktaAuth.signInWithRedirect();
    } catch (error) {
      console.error('Sign in with redirect failed:', error);
      throw error;
    }
  }

  /**
   * Handle the authentication callback
   * Call this in your login callback route
   */
  async handleAuthenticationCallback() {
    try {
      await this.oktaAuth.handleLoginRedirect();
      return true;
    } catch (error) {
      console.error('Authentication callback failed:', error);
      throw error;
    }
  }

  /**
   * Sign out the user
   * @param {boolean} postLogoutRedirectUri - Whether to redirect after logout
   */
  async signOut(postLogoutRedirectUri = window.location.origin) {
    try {
      await this.oktaAuth.signOut({
        postLogoutRedirectUri,
      });
    } catch (error) {
      console.error('Sign out failed:', error);
      throw error;
    }
  }

  /**
   * Get the current authentication state
   */
  async getAuthState() {
    try {
      return await this.oktaAuth.authStateManager.getAuthState();
    } catch (error) {
      console.error('Failed to get auth state:', error);
      return null;
    }
  }

  /**
   * Check if user is authenticated
   */
  async isAuthenticated() {
    try {
      const authState = await this.getAuthState();
      return authState?.isAuthenticated || false;
    } catch (error) {
      console.error('Failed to check authentication:', error);
      return false;
    }
  }

  /**
   * Get the current access token
   */
  async getAccessToken() {
    try {
      const tokenManager = this.oktaAuth.tokenManager;
      const accessToken = await tokenManager.get('accessToken');

      if (!accessToken) {
        console.log('No access token found, attempting to refresh...');
        try {
          await this.renewTokens();
          const newAccessToken = await tokenManager.get('accessToken');
          return newAccessToken?.accessToken || null;
        } catch (refreshError) {
          console.error('Token refresh failed:', refreshError);
          return null;
        }
      }

      return accessToken?.accessToken || null;
    } catch (error) {
      console.error('Failed to get access token:', error);
      return null;
    }
  }

  /**
   * Validate the current token
   */
  async validateToken() {
    try {
      const accessToken = await this.getAccessToken();
      if (!accessToken) {
        console.log('No valid access token found');
        return false;
      }

      const isValid = await this.oktaAuth.token.verify(accessToken);
      if (!isValid) {
        console.log('Token validation failed, attempting refresh...');
        await this.renewTokens();
        const newToken = await this.getAccessToken();
        return !!newToken;
      }

      return true;
    } catch (error) {
      console.error('Token validation error:', error);
      return false;
    }
  }

  /**
   * Get the current ID token
   */
  async getIdToken() {
    try {
      const tokenManager = this.oktaAuth.tokenManager;
      const idToken = await tokenManager.get('idToken');
      return idToken?.idToken || null;
    } catch (error) {
      console.error('Failed to get ID token:', error);
      return null;
    }
  }

  /**
   * Get user information from the ID token
   */
  async getUser() {
    try {
      const user = await this.oktaAuth.getUser();
      return user;
    } catch (error) {
      console.error('Failed to get user info:', error);
      return null;
    }
  }

  /**
   * Renew tokens
   */
  async renewTokens() {
    try {
      console.log('Starting token renewal...');
      const tokenManager = this.oktaAuth.tokenManager;

      // Attempt to renew both tokens
      const [newAccessToken, newIdToken] = await Promise.all([
        tokenManager.renew('accessToken'),
        tokenManager.renew('idToken'),
      ]);

      if (!newAccessToken || !newIdToken) {
        console.error('Token renewal failed: One or both tokens not received');
        return false;
      }

      console.log('Token renewal successful');
      return true;
    } catch (error) {
      console.error('Failed to renew tokens:', error);
      // If token renewal fails, we should clear tokens and redirect to login
      await this.clearTokens();
      window.location.href = '/login';
      return false;
    }
  }

  /**
   * Subscribe to authentication state changes
   * @param {Function} callback - Function to call when auth state changes
   */
  onAuthStateChange(callback) {
    return this.oktaAuth.authStateManager.subscribe(callback);
  }

  /**
   * Unsubscribe from authentication state changes
   * @param {Function} unsubscribe - The unsubscribe function returned by onAuthStateChange
   */
  removeAuthStateChangeListener(unsubscribe) {
    if (typeof unsubscribe === 'function') {
      unsubscribe();
    }
  }

  /**
   * Clear all tokens and user session data
   */
  async clearTokens() {
    try {
      await this.oktaAuth.tokenManager.clear();
      return true;
    } catch (error) {
      console.error('Failed to clear tokens:', error);
      return false;
    }
  }

  /**
   * Get the Okta Auth instance (for advanced usage)
   */
  getOktaAuth() {
    return this.oktaAuth;
  }
}

// Export a singleton instance
export default new OktaAuthService();
export { OktaAuthService };
